import styled from 'styled-components'
import {
  Flex,
  TooltipText,
  IconButton,
  useModal,
  CalculateIcon,
  Skeleton,
  useTooltip,
  Text,
  Box,
} from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import Balance from 'components/Balance'
import RoiCalculatorModal from 'components/RoiCalculatorModal'
import { DeserializedPool } from 'state/types'
import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'utils/bigNumber'
import { vaultPoolConfig } from 'config/constants/pools'
import { usePool } from 'state/pools/hooks'
import { cloneElement, isValidElement, ReactElement, ReactPortal } from 'react'

const ApyLabelContainer = styled(Flex)`
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
`
const InlineText = styled(Text)`
  display: inline;
`

interface AprRowProps {
  pool: DeserializedPool
  stakedBalance: BigNumber
  performanceFee?: number
}

const AprRow: React.FC<AprRowProps> = ({ pool, stakedBalance, performanceFee = 0 }) => {
  const { t } = useTranslation()
  const {
    stakingToken,
    earningToken,
    isFinished,
    apr,
    rawApr,
    earningTokenPrice,
    stakingTokenPrice,
    userData,
    vaultKey,
  } = pool

  const stakingTokenBalance =
    pool.stakingToken.symbol == 'GF'
      ? userData?.gfTokenIds
        ? new BigNumber(
            userData.gfTokenIds.filter(
              (id) => id >= pool.allowedTokenIds?.from && id <= pool.allowedTokenIds?.to,
            ).length,
          )
        : BIG_ZERO
      : userData.stakingTokenBalance

  const tooltipContent = vaultKey ? (
    t('APY includes compounding, APR doesn’t. This pool’s HODLX is compounded automatically, so we show APY.')
  ) : pool.hasNFTRange ? (
    <div>
      <p style={{ paddingBottom: '1rem' }}>This pool has fixed APR depending on the NFT Token ID:</p>
      <ul>
        {pool.sousId === 53 ? (
          <>
            <li>
              NFT number {pool.allowedTokenIds?.from} to {pool.allowedTokenIds?.to}: {pool.aprRange?.from}% APR
            </li>
          </>
        ) : (
          <>
            <li>NFT number 1,800 to 1,899: 200% APR</li>
            <li>NFT number 1,900 to 2,149: 150% APR</li>
            <li>NFT number 2,150 to 2,549: 100% APR</li>
          </>
        )}
      </ul>
    </div>
  ) : (
    <div>
      <p style={{ paddingBottom: '1rem' }}>{`This pool has fixed ${pool.fixedAprValue}% APR`}</p>
    </div>
  )

  const { targetRef, tooltip, tooltipVisible } = useTooltip(tooltipContent, {
    placement: 'bottom-start',
  })

  const apyModalLink = stakingToken.address ? `/swap?outputCurrency=${stakingToken.address}` : '/swap'

  const { pool: gfPool } = usePool(38)
  const normalPrice = 35

  const [onPresentApyModal] = useModal(
    <RoiCalculatorModal
      earningTokenPrice={pool.earningToken.symbol === 'GF' ? normalPrice : earningTokenPrice}
      stakingTokenPrice={pool.stakingToken.symbol === 'GF' ? normalPrice : stakingTokenPrice}
      apr={vaultKey ? rawApr : apr}
      linkLabel={t('Get %symbol%', { symbol: stakingToken.symbol })}
      linkHref={apyModalLink}
      stakingTokenBalance={stakedBalance.plus(stakingTokenBalance)}
      stakingTokenSymbol={stakingToken.name}
      earningTokenSymbol={earningToken.name}
      autoCompoundFrequency={vaultPoolConfig[vaultKey]?.autoCompoundFrequency ?? 0}
      performanceFee={performanceFee}
      decimal={stakingToken.decimals}
      isNft={pool.isNft}
      currentStakedTokens={pool.totalStaked.toNumber() / 10 ** pool.stakingToken.decimals}
      fixedApr={pool.fixedApr}
      isTimeBased={pool.isWithTimes}
    />,
  )

  return (
    <Flex alignItems="center" justifyContent="space-between">
      {tooltipVisible &&
        cloneElement((tooltip as ReactPortal).children as ReactElement<any>, {
          style: { maxWidth: '340px', ...((tooltip as ReactPortal).children as ReactElement<any>).props.style },
        })}
      <Box display="inline">
        <TooltipText display="inline" ref={targetRef}>
          {vaultKey ? `${t('APY')}:` : `${t('APR')}:`}
        </TooltipText>
        <InlineText fontSize="12px" color="textDisabled">
          {` FIXED (PER NFT)`}
        </InlineText>
      </Box>
      {apr || isFinished ? (
        <ApyLabelContainer alignItems="center" onClick={onPresentApyModal}>
          <Balance
            fontSize="16px"
            fontWeight={600}
            isDisabled={isFinished}
            value={
              isFinished
                ? 0
                : pool.fixedAprValue > 0
                ? pool.fixedAprValue
                : userData.stakedBalance > BIG_ZERO
                ? userData.weightedApr
                : pool.aprRange?.to
            }
            decimals={0}
            unit={
              userData.stakedBalance > BIG_ZERO || pool.fixedAprValue > 0
                ? '%'
                : pool.aprRange?.to !== pool.aprRange?.from
                ? `% to ${pool.aprRange?.from}%`
                : `%`
            }
            onClick={onPresentApyModal}
          />
          {!isFinished && (
            <IconButton variant="text" scale="sm">
              <CalculateIcon color="textSubtle" width="18px" />
            </IconButton>
          )}
        </ApyLabelContainer>
      ) : (
        <Skeleton width="82px" height="32px" />
      )}
    </Flex>
  )
}

export default AprRow
